import React, { useEffect, useRef, useCallback } from 'react';
import {
  X,
  Check,
  Copy,
  ChevronRight,
  ChevronDown,
  Plus,
  Trash2,
} from 'lucide-react';
import CodeEditor from './ui/CodeEditor';
import CompactSelectPicker from './ui/CompactSelectPicker';
import OsScroll from './ui/OsScroll';
import { stripBlankKvRows, makeKvRowId } from '../utils/kvRows';

const MOCK_CONTENT_TYPES = [
  'application/json',
  'application/xml',
  'text/plain',
  'text/html',
  'application/javascript',
  'application/octet-stream',
];

export function MockKvRows({ label, rows, onChange, t }) {
  // The trailing "type to add" sentinel row is never persisted (stripBlankKvRows drops it),
  // so its React key must NOT be regenerated on every render. Previously it was minted with
  // `Date.now()`/`makeKvRowId()` inside render, so any re-render of the parent while a mock
  // KV input was focused remounted the input and dropped the caret ("buggy while typing").
  // Keep the sentinel id in a ref so it is stable across renders, and only rotate it once it
  // has been consumed (the user typed a key, so the row got persisted with this id).
  const sentinelIdRef = useRef(makeKvRowId('mkv'));

  const realRows = stripBlankKvRows(
    (rows || []).map((row, i) => ({
      ...row,
      enabled: row.enabled !== false,
      _id: row._id || `mkv-persist-${i}`,
    }))
  );
  if (realRows.some((row) => row._id === sentinelIdRef.current)) {
    sentinelIdRef.current = makeKvRowId('mkv');
  }
  const list = [...realRows];
  if (!list.length || list[list.length - 1].key !== '') {
    list.push({ key: '', value: '', enabled: true, _id: sentinelIdRef.current });
  }

  const commit = (nextRows) => {
    onChange(stripBlankKvRows(nextRows));
  };

  const updateRow = (rowId, patch) => {
    commit(list.map((row) => (row._id === rowId ? { ...row, ...patch } : row)));
  };

  const removeRow = (rowId) => {
    commit(list.filter((row) => row._id !== rowId));
  };

  return (
    <div className="space-y-1">
      <label className="mock-label block">{label}</label>
      <div className="mock-kv-table">
        {list.map((row) => (
          <div key={row._id} className="mock-kv-row">
            <div className="mock-kv-cell">
              <input
                type="text"
                className="mock-input"
                placeholder={t('mockTab.key')}
                value={row.key ?? ''}
                onChange={(e) => updateRow(row._id, { key: e.target.value })}
              />
            </div>
            <div className="mock-kv-cell">
              <input
                type="text"
                className="mock-input"
                placeholder={t('mockTab.value')}
                value={row.value ?? ''}
                onChange={(e) => updateRow(row._id, { value: e.target.value })}
              />
            </div>
            <div className="mock-kv-cell mock-kv-cell--action">
              {(row.key || '').trim() ? (
                <button
                  type="button"
                  className="mock-iconbtn mock-iconbtn--danger"
                  title={t('mockTab.remove')}
                  onClick={() => removeRow(row._id)}
                >
                  <Trash2 size={13} />
                </button>
              ) : (
                <span aria-hidden />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Isolated so it only re-renders when the body text / language actually changes.
// Typing in an example's KV tables re-renders the card but NOT this Monaco editor
// (its `value` and stable `onChange` are unchanged), which was the source of the lag.
const MockBodyEditor = React.memo(function MockBodyEditor({ value, splitLayoutTick, onChange }) {
  return (
    <div className="mock-editor-wrap">
      <CodeEditor
        splitLayoutTick={splitLayoutTick}
        hideChrome
        value={value}
        onChange={onChange}
        language="json"
        height="100%"
      />
    </div>
  );
});

// One response-example card. Memoized so editing example A never re-renders example B
// (and B's Monaco editor). Per-example handlers are stable useCallbacks keyed on ex.id.
const MockExampleCard = React.memo(function MockExampleCard({
  ex,
  collapsed,
  schemas,
  splitLayoutTick,
  updateMockExample,
  removeMockExample,
  duplicateMockExample,
  toggleMockExampleEnabled,
  toggleMockCollapsed,
  t,
}) {
  const id = ex.id;
  const setBody = useCallback((val) => updateMockExample(id, { body: val }), [updateMockExample, id]);
  const setHeaders = useCallback((headers) => updateMockExample(id, { headers }), [updateMockExample, id]);
  const setMatchQuery = useCallback((matchQuery) => updateMockExample(id, { matchQuery }), [updateMockExample, id]);
  const setMatchHeaders = useCallback((matchHeaders) => updateMockExample(id, { matchHeaders }), [updateMockExample, id]);

  return (
    <div className="mock-card">
      <div className="mock-card-header">
        <button
          type="button"
          onClick={() => toggleMockCollapsed(id)}
          className="mock-collapse-btn flex-shrink-0"
          title={collapsed ? t('mockTab.expand') : t('mockTab.collapse')}
          aria-expanded={!collapsed}
        >
          {collapsed ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
        </button>
        <input
          type="text"
          value={ex.name ?? ''}
          onChange={(e) => updateMockExample(id, { name: e.target.value })}
          placeholder={t('mockTab.namePlaceholder')}
          className="mock-input flex-1"
        />
        <input
          type="number"
          value={ex.status ?? 200}
          onChange={(e) => updateMockExample(id, { status: Number(e.target.value) || 200 })}
          title={t('mockTab.status')}
          className="mock-input"
          style={{ width: '4.5rem' }}
        />
        <button
          type="button"
          role="switch"
          aria-checked={ex.enabled !== false}
          onClick={() => toggleMockExampleEnabled(id)}
          title={ex.enabled !== false ? t('mockTab.activeOn') : t('mockTab.activeOff')}
          className={`mock-toggle flex-shrink-0${ex.enabled !== false ? ' mock-toggle--on' : ''}`}
        >
          <span className="mock-toggle-knob" />
        </button>
        <button
          type="button"
          onClick={() => duplicateMockExample(id)}
          title={t('mockTab.duplicate')}
          className="mock-iconbtn flex-shrink-0"
        >
          <Copy size={14} />
        </button>
        <button
          type="button"
          onClick={() => removeMockExample(id)}
          title={t('mockTab.remove')}
          className="mock-iconbtn mock-iconbtn--danger flex-shrink-0"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {!collapsed && (
        <div className="mock-card-body">
          <div className="flex items-center gap-2">
            <label className="mock-label flex-shrink-0">{t('mockTab.source')}</label>
            <div className="mock-source-toggle">
              <button
                type="button"
                className={`mock-source-opt${ex.sourceType !== 'schema' ? ' mock-source-opt--on' : ''}`}
                onClick={() => updateMockExample(id, { sourceType: 'example' })}
              >
                {t('mockTab.sourceExample')}
              </button>
              <button
                type="button"
                className={`mock-source-opt${ex.sourceType === 'schema' ? ' mock-source-opt--on' : ''}`}
                onClick={() => updateMockExample(id, { sourceType: 'schema' })}
              >
                {t('mockTab.sourceSchema')}
              </button>
            </div>
          </div>

          {ex.sourceType === 'schema' ? (
            <div className="space-y-1.5">
              <label className="mock-label block">{t('mockTab.schema')}</label>
              <CompactSelectPicker
                value={ex.schemaId || ''}
                aria-label={t('mockTab.schema')}
                options={
                  (schemas?.items || []).length
                    ? (schemas.items || []).map((s) => ({ value: s.id, label: s.name || 'Schema' }))
                    : [{ value: '', label: t('mockTab.noSchemas') }]
                }
                onChange={(v) => updateMockExample(id, { schemaId: v })}
              />
              <p className="mock-hint">{t('mockTab.schemaGenNote')}</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <label className="mock-label flex-shrink-0">{t('mockTab.contentType')}</label>
                <select
                  value={ex.contentType ?? 'application/json'}
                  onChange={(e) => updateMockExample(id, { contentType: e.target.value })}
                  className="mock-select flex-1"
                >
                  {(MOCK_CONTENT_TYPES.includes(ex.contentType ?? 'application/json')
                    ? MOCK_CONTENT_TYPES
                    : [ex.contentType, ...MOCK_CONTENT_TYPES]
                  ).map((ct) => (
                    <option key={ct} value={ct}>
                      {ct}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mock-label mb-1 block">{t('mockTab.responseBody')}</label>
                <MockBodyEditor
                  value={ex.body ?? ''}
                  splitLayoutTick={splitLayoutTick}
                  onChange={setBody}
                />
              </div>
            </>
          )}

          <MockKvRows label={t('mockTab.responseHeaders')} rows={ex.headers} t={t} onChange={setHeaders} />
          <MockKvRows label={t('mockTab.matchQuery')} rows={ex.matchQuery} t={t} onChange={setMatchQuery} />
          <MockKvRows label={t('mockTab.matchHeaders')} rows={ex.matchHeaders} t={t} onChange={setMatchHeaders} />
        </div>
      )}
    </div>
  );
});

const MockModal = ({
  isOpen,
  onClose,
  responseExamples,
  collapsedMockIds,
  mockUrlCopied,
  setMockUrlCopied,
  mockUrl,
  inCollection,
  mockServerRunning,
  cloudMockEnabled,
  cloudMockUrl,
  schemas,
  splitLayoutTick,
  addMockExample,
  updateMockExample,
  removeMockExample,
  duplicateMockExample,
  toggleMockExampleEnabled,
  toggleMockCollapsed,
  t,
}) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKeyDown, true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="mock-modal-overlay" onClick={onClose}>
      <div
        className="mock-modal-dialog"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="env-modal-header">
          <span className="env-modal-title">{t('request.mock')}</span>
          <button
            type="button"
            className="env-modal-close"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <OsScroll className="mock-modal-body">
          <div className="space-y-4">
            {/* Local Mock URL */}
            <div className="space-y-1.5">
              <label className="mock-label block">{t('mockTab.urlLabel')}</label>
              {mockUrl ? (
                <div className="flex items-center gap-2">
                  <code
                    className="mock-url"
                    title={mockUrl}
                    style={{ opacity: mockServerRunning ? 1 : 0.6 }}
                  >
                    {mockUrl}
                  </code>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard?.writeText(mockUrl);
                      setMockUrlCopied(true);
                      setTimeout(() => setMockUrlCopied(false), 1500);
                    }}
                    className="mock-iconbtn flex-shrink-0"
                    title={t('mockTab.copy')}
                  >
                    {mockUrlCopied ? (
                      <Check size={14} className="text-green-500" />
                    ) : (
                      <Copy size={14} />
                    )}
                  </button>
                </div>
              ) : (
                // In a collection already: the only thing missing is the running server.
                <p className="mock-hint">{t(inCollection ? 'mockTab.serverOff' : 'mockTab.noUrl')}</p>
              )}
              {mockUrl && !mockServerRunning && (
                <p className="mock-hint-warn">{t('mockTab.serverStopped')}</p>
              )}
            </div>

            {/* Cloud Mock URL */}
            {cloudMockEnabled && (
              <div className="space-y-1.5">
                <label className="mock-label block">Cloud Mock URL</label>
                {cloudMockUrl ? (
                  <div className="flex items-center gap-2">
                    <code className="mock-url" title={cloudMockUrl} style={{ opacity: 1 }}>
                      {cloudMockUrl}
                    </code>
                    <button
                      type="button"
                      onClick={() => { navigator.clipboard?.writeText(cloudMockUrl); }}
                      className="mock-iconbtn flex-shrink-0"
                      title={t('mockTab.copy')}
                    >
                      <Copy size={14} />
                    </button>
                  </div>
                ) : (
                  <p className="mock-hint">Save this request to a collection to get a Cloud Mock URL</p>
                )}
              </div>
            )}

            {/* Examples editor */}
            <div className="flex items-center justify-between">
              <span className="mock-section-title">{t('mockTab.examples')}</span>
              <button type="button" onClick={addMockExample} className="mock-add-btn">
                <Plus size={13} /> {t('mockTab.addExample')}
              </button>
            </div>

            {responseExamples.length === 0 ? (
              <p className="mock-empty">{t('mockTab.noExamples')}</p>
            ) : (
              responseExamples.map((ex) => (
                <MockExampleCard
                  key={ex.id}
                  ex={ex}
                  collapsed={collapsedMockIds.has(ex.id)}
                  schemas={schemas}
                  splitLayoutTick={splitLayoutTick}
                  updateMockExample={updateMockExample}
                  removeMockExample={removeMockExample}
                  duplicateMockExample={duplicateMockExample}
                  toggleMockExampleEnabled={toggleMockExampleEnabled}
                  toggleMockCollapsed={toggleMockCollapsed}
                  t={t}
                />
              ))
            )}
          </div>
        </OsScroll>

      </div>
    </div>
  );
};

export default MockModal;
